interface ResolvedItem<T> {
  index: number;
  item: T;
  productId: number;
}

export async function processByProductId<T, TResult>(
  items: readonly T[],
  resolveProductId: (item: T) => Promise<number>,
  processItem: (item: T, productId: number) => Promise<TResult>,
): Promise<Array<PromiseSettledResult<TResult>>> {
  const results: Array<PromiseSettledResult<TResult>> = new Array(items.length);
  const resolvedItems = await Promise.allSettled(
    items.map(async (item, index) => ({
      index,
      item,
      productId: await resolveProductId(item),
    })),
  );

  const itemsByProductId = new Map<number, Array<ResolvedItem<T>>>();

  resolvedItems.forEach((resolvedItem, index) => {
    if (resolvedItem.status === 'rejected') {
      results[index] = resolvedItem;
      return;
    }

    const productItems = itemsByProductId.get(resolvedItem.value.productId) ?? [];
    productItems.push(resolvedItem.value);
    itemsByProductId.set(resolvedItem.value.productId, productItems);
  });

  await Promise.all(
    [...itemsByProductId.values()].map(async (productItems) => {
      for (const { index, item, productId } of productItems) {
        try {
          results[index] = { status: 'fulfilled', value: await processItem(item, productId) };
        } catch (reason) {
          results[index] = { status: 'rejected', reason };
        }
      }
    }),
  );

  return results;
}
